import { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Eye, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import * as Recharts from "recharts";

const {
  ResponsiveContainer,
  ComposedChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  Line,
} = Recharts as any;

interface ViewStats {
  total: number;
  unique: number;
  anonymous: number;
  visitors: { apelido: string | null; created_at: string }[];
  daily: Record<string, number>;
}

interface AnuncioViewsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  anuncioId: string;
  sessionContato?: string;
  sessionToken?: string;
}

const AnuncioViewsDialog = ({
  open,
  onOpenChange,
  anuncioId,
  sessionContato,
  sessionToken,
}: AnuncioViewsDialogProps) => {
  const [stats, setStats] = useState<ViewStats | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !anuncioId || !sessionContato || !sessionToken) return;
    setLoading(true);
    supabase.functions.invoke("anuncio-views", {
      body: { anuncio_ids: [anuncioId], contato: sessionContato, token: sessionToken },
    }).then(({ data }) => {
      if (data?.views?.[anuncioId]) {
        setStats(data.views[anuncioId]);
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [open, anuncioId, sessionContato, sessionToken]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="tibia-border bg-card max-w-md max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()} onPointerDownOutside={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle className="font-pixel text-xs text-primary flex items-center gap-1.5">
            <Eye className="w-4 h-4" /> Desempenho do Anúncio
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            {loading ? "Carregando..." : stats ? (
              <>
                Total: <strong>{stats.total}</strong> • Únicas: <strong>{stats.unique}</strong> • Anônimas: <strong>{stats.anonymous}</strong>
              </>
            ) : "Nenhuma visualização"}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : (
          <>
            {/* Chart */}
            {(() => {
              if (!stats || stats.total === 0) {
                return (
                  <p className="text-xs text-muted-foreground text-center py-6">
                    Sem dados para exibir o gráfico.
                  </p>
                );
              }
              const daily = stats.daily || {};
              const chartData = Object.entries(daily)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([dateStr, count]) => {
                  const [, m, d] = dateStr.split("-");
                  return { dia: `${d}/${m}`, views: count };
                });

              if (chartData.length === 0) {
                return (
                  <p className="text-xs text-muted-foreground text-center py-6">
                    Sem dados para exibir o gráfico.
                  </p>
                );
              }

              const withMA = chartData.map((item, i) => {
                const window = chartData.slice(Math.max(0, i - 2), i + 1);
                const avg = window.reduce((s, x) => s + x.views, 0) / window.length;
                return { ...item, media: Math.round(avg * 10) / 10 };
              });

              return (
                <div className="w-full h-48 mt-2">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={withMA} margin={{ top: 5, right: 10, left: -15, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                      <XAxis
                        dataKey="dia"
                        tick={{ fontSize: 9, fill: "hsl(var(--muted-foreground))" }}
                        axisLine={{ stroke: "hsl(var(--border))" }}
                        tickLine={false}
                      />
                      <YAxis
                        allowDecimals={false}
                        tick={{ fontSize: 9, fill: "hsl(var(--muted-foreground))" }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "hsl(var(--card))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: 4,
                          fontSize: 11,
                          color: "hsl(var(--foreground))",
                        }}
                        labelFormatter={(l) => `Dia ${l}`}
                        formatter={(value: number, name: string) => [
                          `${value}`,
                          name === "media" ? "Média móvel (3d)" : "Visualizações",
                        ]}
                      />
                      <Bar dataKey="views" fill="hsl(var(--primary))" radius={[3, 3, 0, 0]} name="views" />
                      <Line
                        type="monotone"
                        dataKey="media"
                        stroke="hsl(var(--accent-foreground))"
                        strokeWidth={2}
                        dot={false}
                        name="media"
                        strokeDasharray="5 3"
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              );
            })()}

            {/* Visitor list */}
            <div className="flex-1 overflow-y-auto space-y-1.5 max-h-40">
              <p className="font-pixel text-[8px] text-muted-foreground">Visitantes identificados</p>
              {stats?.visitors?.length ? (
                stats.visitors.map((v, i) => (
                  <div key={i} className="flex items-center justify-between tibia-border-inner bg-secondary/20 rounded-sm px-3 py-1.5">
                    <span className="text-xs text-foreground">{v.apelido || "Anônimo"}</span>
                    <span className="text-[10px] text-muted-foreground">
                      {new Date(v.created_at).toLocaleDateString("pt-BR")} {new Date(v.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-xs text-muted-foreground text-center py-2">
                  Nenhum visitante identificado.
                </p>
              )}

              {stats && stats.anonymous > 0 && (
                <div className="flex items-center justify-between tibia-border-inner bg-secondary/20 rounded-sm px-3 py-1.5">
                  <span className="text-xs text-muted-foreground italic">Visitantes não identificados</span>
                  <span className="text-[10px] text-muted-foreground font-bold">{stats.anonymous}</span>
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AnuncioViewsDialog;
